import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, ShoppingCart, X, Plus, Minus, Trash2, Truck } from "lucide-react";
import { useCart } from "../../contexts/CartContext";
import "./store-cart.css";

/* ------------------------------------------------------------------ */
/* Cart drawer — same behaviour as components/cart/CartSidebar.js,    */
/* in the deep-mine design language. Rendered once by MarketingShell,  */
/* so the header bag button opens it on every page. Auto-opens on      */
/* addToCart (CartContext sets isOpen:true when an item is added).     */
/* ------------------------------------------------------------------ */
export default function StoreCartDrawer() {
  const {
    items,
    totalItems,
    subtotal,
    isCartOpen,
    setCartOpen,
    removeFromCart,
    updateQuantity,
    clearCart,
    formatPrice,
    getShippingFee,
    getShippingNote,
    getTotal,
  } = useCart();

  const shippingFee = getShippingFee();
  const total = getTotal();

  // Escape closes; lock body scroll while open (matches the old Sheet)
  useEffect(() => {
    if (!isCartOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setCartOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isCartOpen]);

  const handleQuantityChange = (productId, newQuantity) => {
    updateQuantity(productId, newQuantity);
  };

  return (
    <div className={"st-cartroot" + (isCartOpen ? " is-open" : "")} aria-hidden={!isCartOpen}>
      <div className="st-cartoverlay" onClick={() => setCartOpen(false)} />
      <aside className="st-cartpanel" role="dialog" aria-label="Shopping cart">
        <div className="st-cart__head">
          <div className="st-cart__title">
            <ShoppingBag size={17} />
            Shopping Cart ({totalItems} items)
          </div>
          <button
            type="button"
            className="st-cart__close"
            onClick={() => setCartOpen(false)}
            aria-label="Close cart"
          >
            <X size={16} />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="st-cart__empty">
            <ShoppingCart size={46} className="st-cart__emptyicon" />
            <div className="st-cart__emptytitle">Your cart is empty</div>
            <p className="st-cart__emptytext">Add some beautiful opals to get started</p>
            <Link
              to="/online-store/shop"
              className="u-btn u-btn--primary st-cart__fullbtn"
              onClick={() => setCartOpen(false)}
            >
              Browse Products
            </Link>
          </div>
        ) : (
          <>
            <div className="st-cart__items">
              {items.map((item) => {
                const price = parseFloat(item.product.price || item.product.regular_price || 0);
                const itemTotal = price * item.quantity;
                const productId = item.product._id || item.product.id;

                return (
                  <div key={`${productId}-${item.metal_type || ""}`} className="st-cart__item">
                    <div className="st-cart__thumb">
                      {item.product.images && item.product.images.length > 0 ? (
                        <img
                          src={item.product.images[0].src || item.product.images[0]}
                          alt={item.product.name}
                          onError={(e) => {
                            e.target.src = "https://placehold.co/64/64";
                          }}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <ShoppingBag size={20} />
                      )}
                    </div>
                    <div className="st-cart__info">
                      <div className="st-cart__name">{item.product.name}</div>
                      <div className="st-cart__each">{formatPrice(price)} each</div>
                      {item.metal_type && (
                        <div className="st-cart__metal">{item.metal_type}</div>
                      )}
                      <div className="st-cart__qty">
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(productId, item.quantity - 1)}
                          aria-label="Decrease quantity"
                        >
                          <Minus size={12} />
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(productId, item.quantity + 1)}
                          aria-label="Increase quantity"
                        >
                          <Plus size={12} />
                        </button>
                        <button
                          type="button"
                          className="st-cart__trash"
                          onClick={() => removeFromCart(productId)}
                          aria-label="Remove item"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    <div className="st-cart__linetotal">{formatPrice(itemTotal)}</div>
                  </div>
                );
              })}
            </div>

            <div className="st-cart__summary">
              <div className="st-cart__row">
                <span>Subtotal ({totalItems} items):</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="st-cart__row">
                <span>Shipping:</span>
                <span>{shippingFee === 0 ? "FREE" : formatPrice(shippingFee)}</span>
              </div>
              <div className="st-cart__row st-cart__row--total">
                <span>Total:</span>
                <span>{formatPrice(total)}</span>
              </div>
              <div className="st-cart__note">
                <Truck size={14} />
                <span>{getShippingNote()}</span>
              </div>
              <Link
                to="/checkout"
                className="u-btn u-btn--primary st-cart__fullbtn"
                onClick={() => setCartOpen(false)}
              >
                Proceed to Checkout
              </Link>
              <Link
                to="/online-store/shop"
                className="u-btn u-btn--ghost st-cart__fullbtn"
                onClick={() => setCartOpen(false)}
              >
                Continue Shopping
              </Link>
              {totalItems > 0 && (
                <button
                  type="button"
                  className="st-cart__clear"
                  onClick={() => {
                    clearCart();
                    setCartOpen(false);
                  }}
                >
                  Clear Cart
                </button>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
